/* eslint-disable jira/integration/test-id-by-folder-structure */
/** @jsx jsx */
import { useMemo } from 'react';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { cssMap, jsx } from '@atlaskit/css';
import { FormSection } from '@atlaskit/form/form-section';
import Link from '@atlaskit/link';
import { token } from '@atlaskit/tokens';
import EditIcon from '@atlaskit/icon/core/edit';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { ModalButtonTrigger } from '@atlassian/entry-points/modal-button-trigger';
import {
	ApplicationClientId,
	ApplicationClientSecret,
	CopyableApplicationClientID,
	CopyableApplicationClientSecret,
	RedirectUrl,
	RemoteApplicationScopeMultiSelect,
} from './DisplayUrl.tsx';
import { getApplinksAdminUrl } from './getApplinksAdminUrl.tsx';
import { outgoingCredentialsModalEntryPoint } from './outgoingCredentialsModalEntryPoint.entrypoint.tsx';
import type {
	ApplicationLinkOAuth2ConfigInfo,
	ApplicationLinkV3Data,
} from './CommonApplicationLinkCreationEntity.tsx';

const outgoingCredentialsButtonTextMessage = defineMessage({
	defaultMessage: 'Update outgoing link credentials',
	id: 'applinks.administration.ui.edit.outgoing-credentials-button-text',
	description: 'Label for the button which opens the modal to update outgoing link credentials.',
});

const outgoingLinkTitleMessage = defineMessage({
	defaultMessage: 'Outgoing link',
	id: 'applinks.administration.ui.edit.outgoing-link.title',
	description: 'Title for the section displaying outgoing link details.',
});

const outgoingLinkDescriptionMessage = defineMessage({
	defaultMessage:
		'Find these details in the external application and enter them below. You may need to ask an admin of the external application for these details.',
	id: 'applinks.administration.ui.edit.outgoing-link.description',
	description: 'Description for the section displaying incoming link details.',
});

const incomingLinkTitleMessage = defineMessage({
	defaultMessage: 'Incoming link',
	id: 'applinks.administration.ui.edit.incoming-link.title',
	description: 'Title for the section displaying incoming link details.',
});

const incomingLinkDescriptionMessage = defineMessage({
	defaultMessage:
		"Copy these details to the external application. If you're not an admin of the application, ask an existing admin to make the updates for you.",
	id: 'applinks.administration.ui.edit.incoming-link.description',
	description: 'Description for the section displaying incoming link details.',
});

const goToRemoteSiteLinkMessage = defineMessage({
	defaultMessage: 'Go to {displayUrl}',
	id: 'applinks.administration.ui.edit.go-to-remote-site-link',
	description: 'Clickable link which navigates the user to the site at URL {displayUrl}',
});

const styles = cssMap({
	growField: {
		flexGrow: 1,
	},
	formSectionInner: {
		marginTop: token('space.200'),
	},
});

const OutgoingCredentialsModalButton = (props: {
	applink: ApplicationLinkV3Data;
	onDisplayError: (err: string) => void;
	onRequestStatusRefresh: () => void;
}) => {
	const { formatMessage } = useIntl();

	const entryPointProps = useMemo(
		() => ({
			applink: props.applink,
			onDisplayError: props.onDisplayError,
			onClose: props.onRequestStatusRefresh,
		}),
		[props.applink, props.onDisplayError, props.onRequestStatusRefresh],
	);

	return (
		<ModalButtonTrigger
			testId="applinks-administration.ui.edit.oauth2.credentials-form.outgoing-credentials-modal-button"
			name="updateOutgoingLinkCredentials"
			entryPoint={outgoingCredentialsModalEntryPoint}
			entryPointProps={entryPointProps}
			iconBefore={EditIcon}
		>
			{formatMessage(outgoingCredentialsButtonTextMessage)}
		</ModalButtonTrigger>
	);
};

type CredentialsFormProps = {
	applink: ApplicationLinkV3Data;
	oauth2ConfigInfo: ApplicationLinkOAuth2ConfigInfo;
	partiallyLinkedMode?: boolean;
	onDisplayError: (err: string) => void;
	onRequestStatusRefresh: () => void;
};

const OutgoingScopes = (props: {
	oauth2ConfigInfo: ApplicationLinkOAuth2ConfigInfo;
}): JSX.Element | null => {
	if (props.oauth2ConfigInfo.isRemoteApplicationCloud) {
		return null;
	}
	return (
		<RemoteApplicationScopeMultiSelect
			name="remoteApplicationSupportedScopes"
			supportedScopes={props.oauth2ConfigInfo.remoteApplicationSupportedScopes || []}
			defaultSelected={
				props.oauth2ConfigInfo.outboundScopes?.map((scope) => ({
					label: scope,
					value: scope,
				})) || []
			}
		/>
	);
};

const CloudConnectorCredentialsForm = (props: {
	oauth2ConfigInfo: ApplicationLinkOAuth2ConfigInfo;
}): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Stack>
			<FormSection title={formatMessage(outgoingLinkTitleMessage)}>
				<Stack space="space.200" xcss={styles.formSectionInner}>
					<OutgoingScopes oauth2ConfigInfo={props.oauth2ConfigInfo} />
				</Stack>
			</FormSection>
		</Stack>
	);
};

const StandardCredentialsForm = (props: CredentialsFormProps): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Stack>
			<FormSection
				title={formatMessage(incomingLinkTitleMessage)}
				description={formatMessage(incomingLinkDescriptionMessage)}
			>
				<Stack space="space.200" xcss={styles.formSectionInner}>
					<Inline space="space.200">
						<Box xcss={styles.growField}>
							<CopyableApplicationClientID
								name="copyableApplicationClientId"
								clientId={props.oauth2ConfigInfo.inboundOAuthClientId}
							/>
						</Box>
						<Box xcss={styles.growField}>
							<CopyableApplicationClientSecret
								name="copyableApplicationClientSecret"
								clientSecret={props.oauth2ConfigInfo.inboundOAuthClientSecret}
							/>
						</Box>
					</Inline>
				</Stack>
			</FormSection>
			<FormSection
				title={formatMessage(outgoingLinkTitleMessage)}
				description={formatMessage(outgoingLinkDescriptionMessage)}
			>
				<Stack space="space.200" xcss={styles.formSectionInner}>
					{props.partiallyLinkedMode && (
						<Stack space="space.200">
							<Inline space="space.200">
								<Box xcss={styles.growField}>
									<ApplicationClientId name="applicationClientId" />
								</Box>
								<Box xcss={styles.growField}>
									<ApplicationClientSecret name="applicationClientSecret" />
								</Box>
							</Inline>
						</Stack>
					)}
					{!props.oauth2ConfigInfo.isRemoteApplicationCloud && (
						<RedirectUrl
							name="redirectUrl"
							defaultRedirectUrl={props.oauth2ConfigInfo?.inboundRedirectUrl || ''}
						/>
					)}
					<OutgoingScopes oauth2ConfigInfo={props.oauth2ConfigInfo} />
					{!props.partiallyLinkedMode && (
						<Box>
							<OutgoingCredentialsModalButton
								applink={props.applink}
								onDisplayError={props.onDisplayError}
								onRequestStatusRefresh={props.onRequestStatusRefresh}
							/>
						</Box>
					)}
					<Link
						href={getApplinksAdminUrl(props.applink.displayUrl, props.applink.type)}
						target="_blank"
					>
						{formatMessage(goToRemoteSiteLinkMessage, {
							displayUrl: props.applink.displayUrl,
						})}
					</Link>
				</Stack>
			</FormSection>
		</Stack>
	);
};

const CredentialsForm = (props: CredentialsFormProps): JSX.Element => {
	const isCloudConnector = !!props.applink.properties?.CLOUD_CONNECTOR_PRODUCT_CONNECTION_ID;

	if (isCloudConnector) {
		return <CloudConnectorCredentialsForm oauth2ConfigInfo={props.oauth2ConfigInfo} />;
	}

	return <StandardCredentialsForm {...props} />;
};

export default CredentialsForm;
